import FetchBase from '../Api/FetchBase'
import { observable, action } from 'mobx'
import { API_ENDPOINTS, DEFAULT_PAGE_START } from '../Utils/constants'
import { removeHtmlFromString } from '../Utils/helpers'

export default class CommentsStore extends FetchBase {
  @observable commentsLoader = false
  @observable totalComments = 0
  @observable comments = []
  @observable isUserAssignedInComment = false
  @observable commentsCurrentPage = DEFAULT_PAGE_START
  @observable commentsPriorityLoader = false
  @observable commentsPriority = []
  @observable commentsSearchText = undefined
  @observable commentId = ''
  @observable commentsSearchHTMLText = ''
  @observable commentsDataLoader = false
  @observable commentFilters = {}
  @observable ordering = undefined
  @observable orderingBy = undefined
  @observable commentdata = undefined
  @observable userIds = []
  @observable spaceIds = []

  @observable projectWidgetComments = []
  @observable projectWidgetTotalComments = 0

  @action.bound
  setCommentFilters = (value = {}) => {
    this.commentFilters = value
  }

  @action.bound
  setOrdering = (value = undefined) => {
    this.ordering = value
  }

  @action.bound
  setOrderingBy = (value = undefined) => {
    this.orderingBy = value
  }

  @action.bound
  setCommentsSearchText = (value = undefined) => {
    this.commentsSearchText = value
  }

  //set commentsId for comment view in comment page while user click from home oos full view comment
  @action.bound
  setCommentsId = (commentId = '', text = '') => {
    this.commentId = commentId
    this.commentsSearchHTMLText = text
  }

  @action.bound
  setCommentsCurrentPage = (value = DEFAULT_PAGE_START) => {
    this.commentsCurrentPage = value
  }

  @action.bound
  getCommentsPriority = async (signal, networkOnly = true) => {
    if (!networkOnly && this.commentsPriority?.length) {
      return [true, this.commentsPriority]
    }
    try {
      this.commentsPriorityLoader = true
      let response = await this.get(
        API_ENDPOINTS.GET_COMMENTS_PRIORITY,
        {},
        signal
      )
      if (response.status) {
        this.commentsPriority = response.data
        return [true, response.data]
      }
      this.commentsPriority = []
      return [false, undefined]
    } catch (err) {
      this.commentsPriority = []
      return [false, undefined]
    } finally {
      this.commentsPriorityLoader = false
    }
  }

  @action.bound
  updateCommentLocally = (id, newValue) => {
    this.comments = (this.comments || []).map((comment) => {
      if (comment.id === id) {
        return {
          ...comment,
          ...newValue,
        }
      }
      return comment
    })
  }

  @action.bound
  updateComment = async (id, values) => {
    try {
      this.commentsDataLoader = true
      let response = await this.post(API_ENDPOINTS.UPDATE_COMMENT, {
        id,
        ...values,
      })
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.commentsDataLoader = false
    }
  }

  @action.bound
  getCommentByIdLocally = (id) => {
    return (this.comments || []).find((comment) => comment.id === id)
  }

  @action.bound
  getCommentById = async (id, projectId) => {
    try {
      this.commentsLoader = true
      let response = await this.post(API_ENDPOINTS.GET_COMMENTS_BY_ID, {
        id,
        projectId,
      })
      if (response.status) {
        this.commentdata = response?.data
        return [true, response.data]
      }
      this.commentdata = {}
      return [false, undefined]
    } catch (err) {
      this.commentdata = {}
      return [false, undefined]
    } finally {
      this.commentsLoader = false
    }
  }

  @action.bound
  getAllCommentsExportData = async (
    projectId,
    { exportFileType = 'csv' } = {}
  ) => {
    try {
      let options = this.getFilteredOptions()
      options['exportFileType'] = exportFileType
      let response = await this.post(API_ENDPOINTS.EXPORT_COMMENTS, {
        projectId,
        ...options,
      })
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getFilteredOptions = () => {
    let temp = {}
    this.commentsSearchText && (temp['search'] = this.commentsSearchText)
    let keys = Object.keys(this.commentFilters || {})
    for (let filter of keys) {
      if (this.commentFilters[filter] !== undefined) {
        temp[filter] = this.commentFilters[filter]
      }
    }
    if (this.ordering) {
      temp['ordering'] = this.ordering
      temp['orderBy'] = this.orderingBy
    }
    return temp
  }

  @action.bound
  getAllComments = async (projectId, signal) => {
    try {
      this.commentsLoader = true
      let options = this.getFilteredOptions() || {}
      let response = await this.post(
        API_ENDPOINTS.GET_COMMENTS,
        {
          projectId,
          pageNo: Math.max(this.commentsCurrentPage - 1, 0),
          ...options,
        },
        signal
      )
      if (response.status) {
        this.comments = response?.data?.comments || []
        this.spaceIds = response?.data?.spaceDropdown || []
        this.userIds = response?.data?.userDropdown || []

        this.totalComments = response?.data?.total || 0
        this.isUserAssignedInComment = response?.isUserAssigned
        return [true, response?.data?.comments]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.commentsLoader = false
    }
  }

  @action.bound
  getSearchFilterId = async (value, signal) => {
    try {
      const res = await this.post(
        API_ENDPOINTS.GET_COMMENTS_BY_ID,
        {
          ...value,
        },
        signal
      )
      this.commentsSearchHTMLText = removeHtmlFromString(
        this.commentsSearchHTMLText
      )
      if (res?.status) {
        this.commentsSearchHTMLText = removeHtmlFromString(
          this.commentsSearchHTMLText
        )
        this.comments = [res?.data?.comment] || this.comments
        this.totalComments = 1
        return [true, res?.data?.comment]
      }
    } catch (err) {
      console.log(err)
    }
  }

  @action.bound
  addComment = async (projectId, values) => {
    try {
      this.commentsDataLoader = true
      let response = await this.post(API_ENDPOINTS.ADD_COMMENT, {
        projectId,
        ...values,
      })
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.commentsDataLoader = false
    }
  }

  @action.bound
  deleteComent = async (id, values = {}) => {
    try {
      this.deleteCommentLoader = true
      let response = await this.post(API_ENDPOINTS.DELETE_COMMENT, {
        id,
        ...values,
      })
      if (response.status) {
        return [response.status, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.deleteCommentLoader = false
    }
  }

  @action.bound
  resetCommentFilters = () => {
    this.commentFilters = {}
    this.commentsSearchHTMLText = ''
    this.commentId = ''
  }

  @action.bound
  getProjectCommentWidget = async (projectId, signal) => {
    try {
      let options = {}
      options['orderBy'] = 'createdAt'
      options['ordering'] = 'DESC'
      options['isChangeOrder'] = true
      // options['pageLimitr'] =TOP_RECORDS_LIMIT // use to top recordes

      let response = await this.post(
        API_ENDPOINTS.GET_COMMENTS,
        {
          projectId,
          ...options,
        },
        signal
      )
      if (response?.status) {
        this.projectWidgetComments = response?.data?.comments || []
        this.projectWidgetTotalComments = response?.data?.total || 0
        return [true, response?.data?.comments]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  resetStore = () => {
    this.commentsLoader = false
    this.totalComments = 0
    this.comments = []
    this.isUserAssignedInComment = false
    this.commentsCurrentPage = DEFAULT_PAGE_START
    this.commentsPriorityLoader = false
    this.commentsPriority = []
    this.commentsSearchText = undefined
    this.commentId = ''
    this.commentsSearchHTMLText = ''
    this.commentsDataLoader = false
    this.commentFilters = {}
    this.ordering = undefined
    this.orderingBy = undefined
    this.projectWidgetComments = []
    this.projectWidgetTotalComments = 0
    this.commentdata = undefined
    this.spaceIds = []
    this.userIds = []
  }
}
